import type React from 'react';

// Constellation
import { Box, Button, ButtonGroup, Dropdown, TextButton } from '@zillow/constellation-10';

// Components
import ActiveRentals from '../ActiveRentals';
import Amenities from '../Amenities';
import Bedrooms from '../Bedrooms';
import Distance from '../Distance';
import PropertyType from '../PropertyType';
import OffMarket from '../OffMarket';

// Misc / Utils
import { dotRecipe } from './styles';
import { getActiveFilters } from '../../utils/filterUtils';
import { useAppSelector } from '../../flux/configureStore';
import { augmentError, formatForLog } from '../../utils/errors';
import { logger } from '../../index';

// Types
import type { PartialFilterType } from '../../models/Types';

interface FilterTrayProps {
  activeRentalsLabel: string;
  offMarketLabel: string;
  bedroomsLabel: string;
  propertyTypesLabel: string;
  distanceLabel: string;
  amenitiesLabel: string;
  setIsLoading: (isLoading: boolean) => void;
}

const FilterTray: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<FilterTrayProps>>> = ({
  activeRentalsLabel,
  offMarketLabel,
  bedroomsLabel,
  propertyTypesLabel,
  distanceLabel,
  amenitiesLabel,
  setIsLoading,
}) => {
  const setFilter = useAppSelector((state) => state.setFilter);
  const filter = useAppSelector((state) => state.filter);
  const hasActiveFilters = Boolean(Object.keys(getActiveFilters(filter)).length);
  const { active, inactive } = filter.activeTypes;

  const onFilterChange = async (partialFilterObj?: PartialFilterType) => {
    setIsLoading(true);
    try {
      await setFilter(partialFilterObj);
    } catch (err: any) {
      augmentError(err, {
        root: 'modules/FilterTray#onFilterChange',
      });

      logger.error(formatForLog(err), 'Filter: Error handling filter change in filter Tray');
    }
    setIsLoading(false);
  };

  // Reset all filters
  const handleResetFilters = async () => {
    setIsLoading(true);
    try {
      await setFilter();
    } catch (err) {
      augmentError(err, {
        root: 'components/FilterTray#handleResetFilters',
      });

      logger.error(formatForLog(err), 'Filter: Error handling filter price change');
    }
    setIsLoading(false);
  };

  // component passed as triggered prop must be called functionally (no jsx)
  return (
    <ButtonGroup css={{ marginBottom: '400', marginInline: '400' }} aria-label="Filters">
      <Dropdown.Root>
        <Dropdown.Trigger>
          <Button.Root size="sm">
            <Button.Label css={{ gap: '150', display: 'flex', alignItems: 'center' }}>
              {active && <Box css={dotRecipe.raw({ activeType: 'forRent' })} />}
              {activeRentalsLabel}
            </Button.Label>
          </Button.Root>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content>{ActiveRentals({ onFilterChange })}</Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
      <Dropdown.Root>
        <Dropdown.Trigger>
          <Button.Root size="sm">
            <Button.Label css={{ gap: '150', display: 'flex', alignItems: 'center' }}>
              {inactive && <Box css={dotRecipe.raw({ activeType: 'offMarket' })} />}
              {offMarketLabel}
            </Button.Label>
          </Button.Root>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content>{OffMarket({ onFilterChange })}</Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
      <Dropdown.Root>
        <Dropdown.Trigger>
          <Button size="sm">{bedroomsLabel}</Button>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content>{Bedrooms({ onFilterChange })}</Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
      <Dropdown.Root>
        <Dropdown.Trigger>
          <Button size="sm">{propertyTypesLabel}</Button>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content>{PropertyType({ onFilterChange })}</Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
      <Dropdown.Root>
        <Dropdown.Trigger>
          <Button size="sm">{distanceLabel}</Button>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content>{Distance({ onFilterChange })}</Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
      <Dropdown.Root>
        <Dropdown.Trigger>
          <Button size="sm">{amenitiesLabel}</Button>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content>{Amenities({ onFilterChange })}</Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
      {hasActiveFilters && <TextButton onClick={handleResetFilters}>Reset all</TextButton>}
    </ButtonGroup>
  );
};

export default FilterTray;
